import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Set } from 'immutable';
import ResizableBox from '@saucelabs/sl-component-resizable-box';
import throttle from 'lodash.throttle';
import classNames from 'classnames';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { IconCloseFill, IconDockSidebarFill } from '@saucelabs/ds-icons';
import { Tabs, Tab } from '@saucelabs/sl-component-tabs';
import { Badge } from '@saucelabs/ds-badge';

import Styles from './DeveloperDrawer.styles';
import actions from '../actions';
import constants from '../constants';
import operations from '../operations';
import selectors from '../selectors';
import * as globalSelectors from '../../../selectors';
import { selectors as testConfigurationSelectors } from '../../testConfiguration';
import { selectors as realDeviceSelectionSelectors } from '../../realDeviceSelection';
import { DeviceLogsContainer } from '../../deviceLogs';
import { DeviceDevToolsContainer } from '../../deviceDevTools';
import {
  MANUAL_TEST_EVENTS,
  trackEvent,
  TOOLBAR_TRACKING_NAMES,
} from '../../../tracking';
import { CONFIGURATION_TEST_TYPES } from '../../../constants';
import { ValueOf } from '../../../globalTypes';
import { DeviceDescriptor } from '../../realDeviceSelection/records';
import { GlobalState } from '../../../reducers';

const {
  DEVELOPER_DRAWER_TABS,
  DEVELOPER_DRAWER_DEFAULT_HEIGHT,
  DEVELOPER_DRAWER_DEFAULT_WIDTH,
  RESIZE_THROTTLE_TIMEOUT_MS,
} = constants;

const TAB_SELECT_EVENTS = {
  [DEVELOPER_DRAWER_TABS.DEVICE_LOGS]:
    MANUAL_TEST_EVENTS.SELECT_DEVICE_LOGS_TAB,
  [DEVELOPER_DRAWER_TABS.DEV_TOOLS]:
    MANUAL_TEST_EVENTS.SELECT_DEBUG_DEV_TOOLS_TAB,
};

const context = classNames.bind(Styles);

type DrawerResizeHandleProps = {
  readonly isHorizontal: boolean;
};

const DrawerResizeHandle: React.FC<DrawerResizeHandleProps> = ({ isHorizontal }) => (
  <div
    className={context(Styles['resize-handle'], 'bg-gray-200', {
      [Styles.horizontal]: isHorizontal,
    })}
    data-qa="developer-drawer-resize-handle"
  >
    <div
      className={context(Styles['handle-lines'], 'text-gray-500', {
        [Styles.horizontal]: isHorizontal,
      })}
      data-qa="developer-drawer-resize-handle-lines"
    />
  </div>
);

type DeveloperDrawerProps = {
  readonly devToolsSupported: boolean;
  readonly resizeCallback: () => void;
};

const DeveloperDrawer: React.FC<DeveloperDrawerProps> = ({
  devToolsSupported,
  resizeCallback = () => undefined,
}) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const deviceType = useSelector(testConfigurationSelectors.deviceTypeSelector);
  const testType = useSelector(testConfigurationSelectors.testTypeSelector);
  const deviceDescriptor = useSelector<GlobalState, DeviceDescriptor>(
    globalSelectors.deviceDescriptorSelector,
  );
  const starDevicesCompositeIds = useSelector<GlobalState, Set<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
    realDeviceSelectionSelectors.starDevicesCompositeIdsSelector,
  );
  const {
    currentTab,
    height,
    isHorizontal,
    isOpen,
    width,
  } = useSelector(selectors.developerDrawerSelector);

  // Should only be executed once "on mount"
  useEffect(() => {
    const initialTab = testType === CONFIGURATION_TEST_TYPES.APP ?
      DEVELOPER_DRAWER_TABS.DEVICE_LOGS : DEVELOPER_DRAWER_TABS.DEV_TOOLS;
    dispatch(actions.setDeveloperDrawerCurrentTab(initialTab));
    dispatch(operations.applyCurrentLocalStorageValues());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resizeHandler = (event, direction, refToElement: HTMLElement) => {
    const newValue = isHorizontal ? refToElement.clientHeight : refToElement.clientWidth;

    if (isHorizontal) {
      dispatch(actions.setDeveloperDrawerHeight(newValue > 0 ? `${newValue}px` : DEVELOPER_DRAWER_DEFAULT_HEIGHT));
    } else {
      dispatch(actions.setDeveloperDrawerWidth(newValue > 0 ? `${newValue}px` : DEVELOPER_DRAWER_DEFAULT_WIDTH));
    }

    if (!isOpen && newValue > 0) {
      dispatch(actions.setDeveloperDrawerOpen(true));
      trackEvent(MANUAL_TEST_EVENTS.SHOW_DEVELOPER_OPTIONS);
    }
    if (isOpen && newValue <= 0) {
      dispatch(actions.setDeveloperDrawerOpen(false));
      trackEvent(MANUAL_TEST_EVENTS.HIDE_DEVELOPER_OPTIONS);
    }

    resizeCallback();
  };

  const setCurrentTab = (eventKey: ValueOf<typeof DEVELOPER_DRAWER_TABS>) => {
    dispatch(actions.setDeveloperDrawerCurrentTab(eventKey));
    if (currentTab !== eventKey) {
      const { compositeId, os, osVersion } = deviceDescriptor;

      trackEvent(TAB_SELECT_EVENTS[eventKey], {
        deviceId: compositeId,
        os: `${os} ${osVersion}`,
        deviceType,
        isStarredDevice: starDevicesCompositeIds.has(compositeId),
        testType,
      });
    }
  };

  return (
    <ResizableBox
      customClass={Styles.default}
      handleComponent={{
        left: <DrawerResizeHandle isHorizontal={false} />,
        top: <DrawerResizeHandle isHorizontal />,
      }}
      horizontal={isHorizontal}
      onResize={throttle(resizeHandler, RESIZE_THROTTLE_TIMEOUT_MS)}
      open={isOpen}
      size={isHorizontal ?
        { height, width: '100%' } :
        { height: '100%', width }}
    >
      {isOpen && (
        <div className={classNames(Styles['resizable-box'], 'bg-white')}>
          <Tabs
            activeKey={currentTab}
            animation={false}
            id="toolbar-developer-drawer-tabs"
            mountOnEnter
            onSelect={setCurrentTab}
            unmountOnExit
          >
            <Tab
              eventKey={DEVELOPER_DRAWER_TABS.DEVICE_LOGS}
              title="Device Log"
            >
              <DeviceLogsContainer />
            </Tab>
            {testType === CONFIGURATION_TEST_TYPES.WEB && (
              <Tab
                eventKey={DEVELOPER_DRAWER_TABS.DEV_TOOLS}
                title={(
                  <span className="flex items-center">
                    Dev Tools
                    <Badge
                      className="active ml-1"
                      color="blue"
                      emphasis="high"
                    >
                      NEW
                    </Badge>
                  </span>
                )}
              >
                <DeviceDevToolsContainer devToolsSupported={devToolsSupported} />
              </Tab>
            )}
          </Tabs>
          <div className={Styles['header-buttons-wrapper']}>
            <Tooltip
              delay={500}
              placement="bottom"
              title={`Align ${isHorizontal ? 'Vertically' : 'Horizontally'}`}
            >
              <button
                className={classNames(Styles['header-button'], 'bg-transparent focus:bg-blue-50')}
                data-qa="developer-drawer-change-orientation"
                onClick={() => {
                  dispatch(operations.setAndStoreDeveloperDrawerHorizontal(!isHorizontal));
                  setTimeout(resizeCallback, 0); // enforce next event loop
                  trackEvent(isHorizontal ?
                    TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_ALIGN_VERTICALLY :
                    TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_ALIGN_HORIZONTALLY,
                  { testType, deviceType },
                  );
                }}
                type="button"
              >
                <span>
                  <IconDockSidebarFill className={context(Styles.icon, {
                    [Styles.horizontal]: isHorizontal,
                  })}
                  />
                </span>
              </button>
            </Tooltip>

            <Tooltip
              delay={500}
              placement="bottom"
              title="Close"
            >
              <button
                className={classNames(Styles['header-button'], 'bg-transparent focus:bg-blue-50')}
                data-qa="developer-drawer-close-button"
                onClick={() => {
                  dispatch(actions.setDeveloperDrawerOpen(false));
                  trackEvent(MANUAL_TEST_EVENTS.HIDE_DEVELOPER_OPTIONS);
                  setTimeout(resizeCallback, 0); // enforce next event loop
                }}
                type="button"
              >
                <span><IconCloseFill className={Styles.icon} /></span>
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </ResizableBox>
  );
};

export default DeveloperDrawer;

export {
  DrawerResizeHandle,
};
